import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Volume2 } from "lucide-react";
import { useAuthStore } from "../../state/authStore";
import {
  contentApi,
  publicContentApi,
  VideoDetail,
  Question,
} from "../../api/content";
import { childrenApi, Child } from "../../api/children";
import { sessionsApi, EarnedSticker } from "../../api/sessions";
import { pairingApi } from "../../api/pairing";
import { playlistsApi, Playlist } from "../../api/playlists";
import { useSessionSocket } from "../../hooks/useSessionSocket";
import { stubIcon } from "../../icons/stubLibrary";
import { optionPictureUrl } from "../../icons/optionPicture";
import { getApiBaseUrl } from "../../api/client";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { WebVideoArt } from "../components/WebVideoArt";
import { WebBedtimeAudio } from "./WebBedtimeAudio";
import { prettyTitle } from "../prettyTitle";
import { videoMinutes } from "../formatDuration";
import { WebVideo } from "../components/WebVideoCard";
import { webAlert } from "../webDialog";

// Web Speech API helper — text-to-speech for the question prompt. Chromium
// 76+ (Tizen/webOS floor) supports `window.speechSynthesis` natively; on
// native iOS/Android phones there's no synth available, so we gracefully
// no-op. The parent toggles this off in Me → Read questions aloud.
function speakText(
  text: string,
  cbs?: { onStart?: () => void; onEnd?: () => void },
) {
  if (typeof window === "undefined") return;
  const synth = (window as any).speechSynthesis;
  const Utt = (window as any).SpeechSynthesisUtterance;
  if (!synth || !Utt) {
    // No synth (native phones) — still fire the lifecycle so the mascot
    // animation runs for a beat instead of never settling.
    cbs?.onStart?.();
    cbs?.onEnd?.();
    return;
  }
  try {
    synth.cancel(); // stop any in-flight utterance
    const u = new Utt(text);
    u.rate = 0.9; // a touch slower — this is teaching narration
    u.pitch = 1.1;
    u.volume = 1;
    u.lang = "en-US";
    if (cbs?.onStart) u.onstart = cbs.onStart;
    if (cbs?.onEnd) {
      u.onend = cbs.onEnd;
      u.onerror = cbs.onEnd;
    }
    synth.speak(u);
  } catch {
    // Tizen sometimes throws on the first call before voices load; ignore.
    cbs?.onEnd?.();
  }
}

type Props = {
  video: WebVideo;
  onClose: () => void;
  // Switch the player to another video/audio (Up-next + auto-advance). The
  // shell swaps its active video in place, so this keeps the player open and
  // preserves where Close returns to.
  onPlay?: (v: WebVideo) => void;
  // TV-show mode: the ordered episodes of the show being watched. When set it
  // becomes the up-next/auto-advance queue (in episode order, no wrap — the
  // show stops after its finale instead of looping to episode 1).
  queue?: WebVideo[];
  // Optional: when web shell already knows which child the parent is
  // viewing as, pre-select them for the "Play on TV" command so we don't
  // have to prompt. If not provided we fall back to "first child" / a prompt.
  activeChildId?: string | null;
  // Report the backend session id this player creates (and null when it ends)
  // so the shell can exclude our own playback from the co-watch banner — our
  // session is echoed back over the socket and would otherwise read as
  // "watching on the TV" pointing at this same browser.
  onLocalSession?: (sessionId: string | null) => void;
};

type AnswerState =
  | { status: "unanswered" }
  | { status: "correct"; selected: number }
  | { status: "wrong"; selected: number };

type NormalizedOption = { label: string; iconKey?: string; imageUrl?: string };

function parseOptions(raw: unknown): NormalizedOption[] {
  // Options can arrive as:
  //   - string[]                       (legacy seed rows)
  //   - { label, iconKey? }[]          (current schema)
  //   - stringified JSON of either
  // We normalize to `{ label, iconKey? }` so the renderer can branch on the
  // iconKey for the stub-library image path while still falling through to
  // the existing auto-detection for legacy / plain-string rows.
  let arr: unknown[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      arr = Array.isArray(p) ? p : [];
    } catch {
      arr = [];
    }
  }
  return arr.map((o) => {
    if (typeof o === "string") return { label: o };
    if (o && typeof o === "object" && "label" in o) {
      const obj = o as {
        label?: unknown;
        iconKey?: unknown;
        imageUrl?: unknown;
      };
      const label = typeof obj.label === "string" ? obj.label : "";
      const iconKey =
        typeof obj.iconKey === "string" && obj.iconKey.length > 0
          ? obj.iconKey
          : undefined;
      const imageUrl =
        typeof obj.imageUrl === "string" && obj.imageUrl.length > 0
          ? obj.imageUrl
          : undefined;
      return { label, iconKey, imageUrl };
    }
    return { label: String(o) };
  });
}

// Auto-derive a visual hint from an option's text so non-readers can answer
// from picture alone. Mirrors `parseOption` in `kid-question.jsx` and the
// RN port in `QuestionOverlay.tsx`.
const NUM_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};
const COLOR_HEX: Record<string, string> = {
  red: "#FF6B6B",
  blue: "#5B8DEF",
  yellow: "#FFD86B",
  green: "#7AC74F",
  orange: "#FF8A4C",
  pink: "#F18EB6",
  purple: "#8C7DD9",
  brown: "#A07248",
  white: "#FFFFFF",
  black: "#2E2316",
};

type OptionHint =
  | { kind: "count"; n: number }
  | { kind: "color"; hex: string }
  | { kind: "letter"; ch: string }
  | { kind: "text" };

function parseOption(text: string): OptionHint {
  const t = String(text || "")
    .trim()
    .toLowerCase();
  if (NUM_WORDS[t] != null) return { kind: "count", n: NUM_WORDS[t] };
  if (/^\d+$/.test(t))
    return { kind: "count", n: Math.min(10, parseInt(t, 10)) };
  if (COLOR_HEX[t]) return { kind: "color", hex: COLOR_HEX[t] };
  if (/^[a-z]$/.test(t)) return { kind: "letter", ch: text.toUpperCase() };
  return { kind: "text" };
}

// Friendly cookie-dot arrangements for 1..6.
const COUNT_LAYOUTS: Record<number, [number, number][]> = {
  1: [[0, 0]],
  2: [
    [-0.7, 0],
    [0.7, 0],
  ],
  3: [
    [-0.9, 0],
    [0, 0],
    [0.9, 0],
  ],
  4: [
    [-0.7, -0.5],
    [0.7, -0.5],
    [-0.7, 0.5],
    [0.7, 0.5],
  ],
  5: [
    [-0.9, -0.5],
    [0, -0.5],
    [0.9, -0.5],
    [-0.5, 0.6],
    [0.5, 0.6],
  ],
  6: [
    [-0.9, -0.5],
    [0, -0.5],
    [0.9, -0.5],
    [-0.9, 0.5],
    [0, 0.5],
    [0.9, 0.5],
  ],
};

function OptionArt({ hint }: { hint: OptionHint }) {
  if (hint.kind === "count") {
    const n = Math.max(1, Math.min(6, hint.n));
    return (
      <svg
        viewBox="-3 -2 6 4"
        width="100%"
        height="100%"
        style={{ display: "block" }}
      >
        {COUNT_LAYOUTS[n].map(([x, y], i) => (
          <g key={i} transform={`translate(${x},${y})`}>
            <circle r="0.74" fill="#C58850" />
            <circle r="0.6" fill="#E1A668" />
            <circle cx="-0.18" cy="-0.18" r="0.10" fill="#5b3a1a" />
            <circle cx="0.20" cy="-0.04" r="0.10" fill="#5b3a1a" />
            <circle cx="-0.10" cy="0.22" r="0.10" fill="#5b3a1a" />
          </g>
        ))}
      </svg>
    );
  }
  if (hint.kind === "color") {
    return (
      <div
        style={{
          width: "72%",
          height: "72%",
          borderRadius: "50%",
          background: hint.hex,
          boxShadow:
            "0 6px 0 rgba(0,0,0,0.10), inset 0 -8px 0 rgba(0,0,0,0.10)",
          border: hint.hex === "#FFFFFF" ? "3px solid var(--line-strong)" : "0",
        }}
      />
    );
  }
  if (hint.kind === "letter") {
    return (
      <div
        style={{
          fontFamily: "var(--display)",
          fontWeight: 900,
          fontSize: "clamp(72px, 12vw, 140px)",
          color: "var(--primary)",
          lineHeight: 0.85,
          textShadow: "0 6px 0 rgba(180,90,36,0.12)",
        }}
      >
        {hint.ch}
      </div>
    );
  }
  return null;
}

function fmtClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Web Player — simulated playback with question overlay.
// The web doesn't actually stream HLS for parents (that's a TV experience);
// instead we tick a counter at 1x speed and trigger questions at their
// timestamps so parents can preview the interactive experience.
// Decode JWT sub claim — same self-heal pattern used elsewhere when the
// auth store predates `parentId` being persisted.
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = typeof atob === "function" ? atob(padded) : "";
    const payload = JSON.parse(decoded);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

export function WebPlayer({
  video,
  onClose,
  onPlay,
  activeChildId,
  onLocalSession,
  queue,
}: Props) {
  const { accessToken, parentId, subscription, ttsEnabled, setSubscription } =
    useAuthStore();
  const effectiveParentId = parentId ?? decodeJwtSub(accessToken);
  const [detail, setDetail] = useState<VideoDetail | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [tvOnline, setTvOnline] = useState(false);
  const [casting, setCasting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState<AnswerState>({ status: "unanswered" });
  // Real backend session for this playback, created lazily on the first
  // answer so a kid watching on web actually accrues stars + stickers (the
  // player is otherwise a simulated preview). Best-effort: if we can't start
  // a session (no child, screen-time limit, demo video) we fall back to a
  // local-only resolution so the quiz never breaks.
  const sessionIdRef = useRef<string | null>(null);
  const startingSessionRef = useRef<Promise<string | null> | null>(null);
  const secondsRef = useRef(0);
  // Sticker(s) just unlocked by an answer on web — drives a celebratory toast.
  const [webUnlocked, setWebUnlocked] = useState<EarnedSticker[]>([]);
  // True while the mascot is narrating the question prompt — drives the
  // "talking" bob animation on the mascot in the question popup.
  const [speaking, setSpeaking] = useState(false);
  // Set after the kid sits on a question without answering — pulses the
  // correct tile and re-says the prompt as a gentle hint.
  const [showHint, setShowHint] = useState(false);
  const triggeredRef = useRef<Set<string>>(new Set());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // The whole player container — passed to the Fullscreen API so the
  // controls bar + question overlay stay visible while fullscreen.
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-hide controls (close button + bottom bar) after 3s of inactivity,
  // mirroring native video-player behavior. Stays visible while paused or
  // a question is showing — otherwise the parent loses the only escape
  // hatches mid-question.
  const [controlsVisible, setControlsVisible] = useState(true);
  const bumpControls = useCallback(() => setControlsVisible(true), []);

  // Sync state with the browser's fullscreen status — needed because the
  // user can also exit via Esc, in which case we never get a button click.
  useEffect(() => {
    const handler = () =>
      setIsFullscreen(document.fullscreenElement === playerRef.current);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = playerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  // Two-effect auto-hide:
  // 1. Whenever controls are visible AND playback is live, schedule a 3s
  //    hide. Re-running on every controlsVisible flip means bumpControls
  //    triggers a fresh timer automatically.
  // 2. Anytime paused or a question lands, force visible (no timer).
  useEffect(() => {
    if (paused || activeQuestion || !controlsVisible) return;
    const t = setTimeout(() => setControlsVisible(false), 3000);
    return () => clearTimeout(t);
  }, [paused, activeQuestion, controlsVisible]);

  useEffect(() => {
    if (paused || activeQuestion) setControlsVisible(true);
  }, [paused, activeQuestion]);
  const hlsUrl = detail?.hlsUrl ?? "";
  const hasVideo = hlsUrl.length > 0;
  // Private (unpublished) videos — e.g. the parent's own generation — gate
  // their bytes on a token at /v1/videos/:id/file. A <video src> can't send
  // headers, so append ?token=. Harmless for public-library videos (the gate
  // is skipped for them).
  const playbackUrl =
    hlsUrl && accessToken && /\/v1\/videos\/[^/]+\/file(?:\?|$)/.test(hlsUrl)
      ? `${hlsUrl}${hlsUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(
          accessToken,
        )}`
      : hlsUrl;

  // Bedtime category → audio-only "screen rests" mode (mirrors the native
  // VoiceBedtimeScreen). The <video> keeps playing for its audio track but is
  // visually hidden behind a dark calm panel so the screen doesn't stimulate
  // at bedtime. Triggered purely by the video carrying the "bedtime" category.
  const isBedtime = (detail?.categories ?? []).some(
    (c) => typeof c === "string" && c.toLowerCase() === "bedtime",
  );

  // True once we know the video is paywalled for this caller. Set either
  // up-front from the `video.isPremium` hint + local sub state, or via a
  // 403 PREMIUM_REQUIRED from the backend (which is the source of truth).
  const [paywalled, setPaywalled] = useState(false);

  // Fetch the full video (with questions) when this surface mounts. Guests
  // (no token) load via the public free-library endpoint — which only serves
  // non-premium, opted-in videos — so they can watch without an account.
  useEffect(() => {
    let cancelled = false;
    setPaywalled(false);
    const fetchDetail = accessToken
      ? contentApi.get(video.id, accessToken)
      : publicContentApi.get(video.id);
    fetchDetail
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Backend says this is premium-gated. Show the paywall instead of
        // an empty player. The error message string is the contract
        // (`PREMIUM_REQUIRED`) — we don't have ApiError import-stable here
        // so just match by substring + 403.
        const status =
          e && typeof e === "object" && "status" in e
            ? (e as { status?: number }).status
            : undefined;
        if (
          status === 403 ||
          (e instanceof Error && /PREMIUM_REQUIRED/.test(e.message))
        ) {
          setPaywalled(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [video.id, accessToken]);

  // Pre-emptive client-side check: when we know the video is premium and
  // the local sub state says we're not entitled, show the paywall before
  // the backend round-trip resolves. The backend gate above is still the
  // security boundary.
  useEffect(() => {
    if (video.isPremium && !(subscription?.isActive ?? false)) {
      setPaywalled(true);
    }
  }, [video.isPremium, subscription?.isActive]);

  // ── Related / Up-next ──────────────────────────────────────────────
  // Pull the catalog once so we can offer "more like this" + auto-advance.
  // We keep the same media type as the current item (audio stays with audio,
  // video with video) and float same-category items to the top.
  const [related, setRelated] = useState<WebVideo[]>([]);
  useEffect(() => {
    let cancelled = false;
    const p = accessToken
      ? contentApi.list({}, accessToken)
      : publicContentApi.list();
    p.then((r) => {
      if (cancelled) return;
      setRelated(
        r.videos.map((v) => ({
          id: v.id,
          title: prettyTitle(v.title) || v.title,
          category: v.categories?.[0] ?? "Video",
          duration: v.duration,
          isPremium: v.isPremium,
          ageMin: v.ageMin,
          ageMax: v.ageMax,
          thumbUrl: v.thumbUrl ?? null,
        })),
      );
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const isBedVideo = useCallback(
    (v: WebVideo) => (v.category ?? "").toLowerCase() === "bedtime",
    [],
  );
  // A STABLE, same-type playlist in catalog order. Keeping the order
  // independent of the current item is what makes next/prev step through the
  // list sequentially instead of bouncing between the same two tracks.
  // In TV-show mode the queue (ordered episodes) IS the playlist; otherwise
  // fall back to the same-type catalog list.
  const isSeriesQueue = Array.isArray(queue) && queue.length > 0;
  const playlist = useMemo(
    () =>
      isSeriesQueue
        ? (queue as WebVideo[])
        : related.filter((v) => (isBedtime ? isBedVideo(v) : !isBedVideo(v))),
    [isSeriesQueue, queue, related, isBedtime, isBedVideo],
  );
  const curIdx = useMemo(
    () => playlist.findIndex((v) => v.id === video.id),
    [playlist, video.id],
  );
  // Series queue steps in order and STOPS at the finale (no wrap); the catalog
  // list wraps so next/prev cycle endlessly.
  const nextItem =
    playlist.length === 0
      ? null
      : isSeriesQueue
        ? curIdx >= 0 && curIdx + 1 < playlist.length
          ? playlist[curIdx + 1]
          : null
        : playlist[curIdx >= 0 ? (curIdx + 1) % playlist.length : 0];
  const prevItem =
    playlist.length === 0
      ? null
      : isSeriesQueue
        ? curIdx > 0
          ? playlist[curIdx - 1]
          : null
        : playlist[
            curIdx >= 0 ? (curIdx - 1 + playlist.length) % playlist.length : 0
          ];
  // "Up next" list — the upcoming items in play order, current excluded. Wraps
  // for the catalog; for a show it's just the remaining episodes.
  const upNext = useMemo(() => {
    if (playlist.length === 0) return [];
    if (isSeriesQueue) {
      return curIdx >= 0
        ? playlist.slice(curIdx + 1, curIdx + 1 + 12)
        : playlist.filter((v) => v.id !== video.id).slice(0, 12);
    }
    if (curIdx < 0) {
      return playlist.filter((v) => v.id !== video.id).slice(0, 12);
    }
    const ordered: WebVideo[] = [];
    for (let i = 1; i < playlist.length; i++) {
      ordered.push(playlist[(curIdx + i) % playlist.length]);
    }
    return ordered.slice(0, 12);
  }, [playlist, curIdx, video.id, isSeriesQueue]);

  // Reset transient playback state when the active item switches in place
  // (Up-next click / auto-advance swap the `video` prop without remounting).
  useEffect(() => {
    setSeconds(0);
    setPaused(false);
    setActiveQuestion(null);
    setAnswer({ status: "unanswered" });
    triggeredRef.current = new Set();
  }, [video.id]);

  const startCheckout = useCallback(async () => {
    if (!accessToken) return;
    try {
      const mod = await import("../../api/subscriptions");
      const hasCustomer =
        subscription && subscription.status !== "inactive";
      const { url } = hasCustomer
        ? await mod.subscriptionsApi.portal(accessToken)
        : await mod.subscriptionsApi.checkout(accessToken);
      window.location.href = url;
    } catch (e) {
      webAlert(
        e instanceof Error ? e.message : "Couldn't reach billing right now.",
      );
    }
  }, [accessToken, subscription]);

  // No-card 30-day trial: grant entitlement instantly, drop the fresh
  // SubscriptionState into the store, and lift the paywall in place — no
  // Stripe redirect, no card. Eligibility is `canStartFreeTrial` (undefined
  // on older servers → treat a never-subscribed parent as eligible).
  const trialEligible =
    subscription?.canStartFreeTrial ??
    (!subscription || subscription.status === "inactive");
  const startFreeTrial = useCallback(async () => {
    if (!accessToken) return;
    try {
      const mod = await import("../../api/subscriptions");
      const next = await mod.subscriptionsApi.startTrial(accessToken);
      setSubscription(next);
      setPaywalled(false);
    } catch (e) {
      webAlert(
        e instanceof Error ? e.message : "Couldn't start your free trial.",
      );
    }
  }, [accessToken, setSubscription]);

  // Children — needed so "Play on TV" can attach a childId. The web shell
  // usually has these too but we re-fetch to keep this component
  // self-contained.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    childrenApi
      .list(accessToken)
      .then((list) => {
        if (!cancelled) setChildren(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Seed TV status from REST; socket keeps it fresh while this view is up.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    pairingApi
      .status(accessToken)
      .then((s) => {
        if (!cancelled) setTvOnline(!!s.online && !!s.paired);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const { sendParentCommand } = useSessionSocket({
    parentId: effectiveParentId ?? null,
    role: "parent",
    onTvStatus: useCallback((s: { online: boolean }) => {
      setTvOnline(!!s.online);
    }, []),
  });

  const handlePlayOnTv = useCallback(() => {
    if (!tvOnline) {
      if (typeof window !== "undefined") {
        webAlert("No TV connected — pair a TV first.");
      }
      return;
    }
    const targetChild =
      children.find((c) => c.id === activeChildId) ?? children[0];
    if (!targetChild) {
      if (typeof window !== "undefined") {
        webAlert("Add a child profile first.");
      }
      return;
    }
    sendParentCommand({
      action: "play_video",
      videoId: video.id,
      childId: targetChild.id,
    });
    setCasting(true);
    setTimeout(() => setCasting(false), 1500);
  }, [tvOnline, children, activeChildId, sendParentCommand, video.id]);

  // ── Add to playlist ──────────────────────────────────────────────────
  // Wired to the real playlists API: open a picker of the parent's
  // playlists (or create a new one) and append this video. The backend
  // add is idempotent, so re-adding an existing video is a no-op.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [plBusy, setPlBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  }, []);

  const openPlaylistPicker = useCallback(async () => {
    if (!accessToken) return;
    setPickerOpen(true);
    setPlaylists(null); // loading
    try {
      setPlaylists(await playlistsApi.list(accessToken));
    } catch {
      setPlaylists([]);
    }
  }, [accessToken]);

  const addToPlaylist = useCallback(
    async (pl: Playlist) => {
      if (!accessToken || plBusy) return;
      setPlBusy(true);
      try {
        await playlistsApi.addItems(pl.id, [video.id], accessToken);
        setPickerOpen(false);
        showToast(`Added to “${pl.name}”`);
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Couldn't add to playlist");
      } finally {
        setPlBusy(false);
      }
    },
    [accessToken, plBusy, video.id, showToast],
  );

  const createAndAdd = useCallback(async () => {
    const name = newName.trim();
    if (!accessToken || plBusy || !name) return;
    setPlBusy(true);
    try {
      const pl = await playlistsApi.create({ name }, accessToken);
      await playlistsApi.addItems(pl.id, [video.id], accessToken);
      setNewName("");
      setPickerOpen(false);
      showToast(`Added to “${pl.name}”`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Couldn't create playlist");
    } finally {
      setPlBusy(false);
    }
  }, [accessToken, plBusy, newName, video.id, showToast]);

  const duration = detail?.duration ?? video.duration ?? 360;
  const questions = detail?.questions ?? [];
  const progress = Math.min(1, seconds / Math.max(duration, 1));

  useEffect(() => {
    if (hasVideo) return;
    if (paused || activeQuestion) return;
    const id = setInterval(() => {
      setSeconds((s) => Math.min(duration, s + 1));
    }, 1000);
    return () => clearInterval(id);
  }, [hasVideo, paused, activeQuestion, duration]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused || activeQuestion) {
      v.pause();
    } else {
      v.play().catch(() => {});
    }
  }, [paused, activeQuestion, hlsUrl]);

  // Trigger questions when their timestamp arrives. A question fires as soon
  // as the playhead reaches its `tSec` — there's no upper window, so seeking
  // *past* a question still surfaces it (the old `< tSec + 3` bound silently
  // dropped any question you scrubbed over). If a forward seek jumps over
  // several at once we show the latest reached checkpoint and consume the
  // rest, so you get one question instead of a backlog.
  useEffect(() => {
    if (activeQuestion) return;
    // Bedtime audio-only is a calm, no-interruption experience — never
    // surface mid-story quiz questions (mirrors native PlayerScreen).
    if (isBedtime) return;
    const reached = questions.filter(
      (q) => !triggeredRef.current.has(q.id) && seconds >= q.tSec,
    );
    if (reached.length === 0) return;
    reached.sort((a, b) => a.tSec - b.tSec);
    const fire = reached[reached.length - 1];
    for (const q of reached) triggeredRef.current.add(q.id);
    setActiveQuestion(fire);
    setAnswer({ status: "unanswered" });
  }, [seconds, questions, activeQuestion, isBedtime]);

  // Backward seek: re-arm questions that are now in the future again, so
  // scrubbing back lets them fire a second time. Without this, once a
  // question is consumed it could never reappear even after rewinding past it.
  // +2s hysteresis so normal 1x ticking never re-arms the one we just answered.
  useEffect(() => {
    for (const q of questions) {
      if (seconds + 2 < q.tSec && triggeredRef.current.has(q.id)) {
        triggeredRef.current.delete(q.id);
      }
    }
  }, [seconds, questions]);

  // Keep the latest elapsed seconds in a ref so the unmount cleanup can end
  // the session with a non-zero watch time (which is what makes it count as a
  // "video watched" for the First Show / Movie Buff stickers).
  useEffect(() => {
    secondsRef.current = seconds;
  }, [seconds]);

  // Lazily create (once) a real backend session for this playback. Returns
  // the session id, or null if we can't/shouldn't record (guest, no child,
  // demo, or the screen-time gate refused).
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionIdRef.current) return sessionIdRef.current;
    if (!accessToken || !activeChildId) return null;
    if (startingSessionRef.current) return startingSessionRef.current;
    const p = (async () => {
      try {
        const { session } = await sessionsApi.start(
          activeChildId,
          video.id,
          accessToken,
        );
        sessionIdRef.current = session.id;
        onLocalSession?.(session.id);
        return session.id;
      } catch {
        return null;
      }
    })();
    startingSessionRef.current = p;
    return p;
  }, [accessToken, activeChildId, video.id]);

  const handleAnswer = (i: number) => {
    if (!activeQuestion || answer.status !== "unanswered") return;
    const correct = i === activeQuestion.correct;
    // Instant local feedback — never wait on the network for the kid's UI.
    setAnswer(
      correct
        ? { status: "correct", selected: i }
        : { status: "wrong", selected: i },
    );

    // Record the answer to the backend so the star counts and any sticker
    // unlocks. Fire-and-forget: failures fall back to the local-only feedback
    // already shown above.
    const q = activeQuestion;
    void (async () => {
      const sid = await ensureSession();
      if (!sid) return;
      try {
        const res = await sessionsApi.submitAnswer(
          sid,
          q.id,
          i,
          accessToken as string,
        );
        if (res.newStickers && res.newStickers.length > 0) {
          setWebUnlocked(res.newStickers);
          window.setTimeout(() => setWebUnlocked([]), 4000);
        }
      } catch {
        /* keep the local feedback */
      }
    })();

    // Auto-close 1.6s after answer
    window.setTimeout(() => {
      setActiveQuestion(null);
      setAnswer({ status: "unanswered" });
    }, 1600);
  };

  // End the session on unmount with the elapsed watch time, so the video
  // counts toward watch-based stickers. Best-effort.
  useEffect(() => {
    return () => {
      const sid = sessionIdRef.current;
      if (sid && accessToken) {
        void sessionsApi
          .end(sid, Math.max(1, Math.round(secondsRef.current)), accessToken)
          .catch(() => {});
      }
      // Clear the shell's "local session" marker so a later genuinely-remote
      // session can light the co-watch banner again.
      onLocalSession?.(null);
    };
  }, [accessToken, onLocalSession]);

  // Once finished, stop the ticker visually
  useEffect(() => {
    if (seconds >= duration) setPaused(true);
  }, [seconds, duration]);

  const questionOptions = useMemo(
    () => (activeQuestion ? parseOptions(activeQuestion.options) : []),
    [activeQuestion],
  );
  // Every answer is a real picture (frame / uploaded / scene image)? Then this
  // is a pre-reader, pictures-only question — drop the white question card and
  // the cream tile chrome and let the bare images stand on their own.
  const allImages = useMemo(
    () =>
      questionOptions.length > 0 && questionOptions.every((o) => !!o.imageUrl),
    [questionOptions],
  );

  // When a question lands, the mascot reads the prompt aloud and animates
  // (teaching narration) — gated on the parent's "Read questions aloud"
  // toggle. A short delay lets the popup animate in first. Cancel any
  // in-flight synth on unmount / question change so utterances never overlap.
  useEffect(() => {
    if (!activeQuestion || !ttsEnabled) return;
    const prompt = (activeQuestion.prompt ?? "").trim();
    if (!prompt) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      speakText(prompt, {
        onStart: () => !cancelled && setSpeaking(true),
        onEnd: () => !cancelled && setSpeaking(false),
      });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      setSpeaking(false);
      if (typeof window === "undefined") return;
      const synth = (window as any).speechSynthesis;
      try {
        synth?.cancel?.();
      } catch {
        // Ignore — Tizen occasionally throws before voices load.
      }
    };
  }, [activeQuestion, ttsEnabled]);

  // Let the kid tap the mascot to hear the prompt again.
  const repeatPrompt = useCallback(() => {
    const prompt = (activeQuestion?.prompt ?? "").trim();
    if (!prompt) return;
    speakText(prompt, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
    });
  }, [activeQuestion]);

  // Inactivity hint: if the kid sits on a question for ~8s without answering,
  // pulse the correct tile and re-say the prompt as a gentle nudge. Resets
  // whenever the question changes or is answered.
  useEffect(() => {
    setShowHint(false);
    if (!activeQuestion || answer.status !== "unanswered") return;
    const t = window.setTimeout(() => {
      setShowHint(true);
      if (ttsEnabled) {
        const prompt = (activeQuestion.prompt ?? "").trim();
        if (prompt) {
          speakText(prompt, {
            onStart: () => setSpeaking(true),
            onEnd: () => setSpeaking(false),
          });
        }
      }
    }, 8000);
    return () => window.clearTimeout(t);
  }, [activeQuestion, answer.status, ttsEnabled]);

  return (
    <div className="w-player-wrap fadeup">
      {webUnlocked.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 18,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderRadius: 999,
            background: "#fff",
            boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
            border: "2px solid var(--primary)",
          }}
        >
          <span style={{ fontSize: 30 }}>{webUnlocked[0].emoji}</span>
          <span>
            <span
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.1em",
                color: "var(--primary-ink)",
              }}
            >
              NEW STICKER!
            </span>
            <span
              style={{
                fontFamily: "var(--display)",
                fontSize: 16,
                fontWeight: 900,
                color: "var(--ink)",
              }}
            >
              {webUnlocked.length === 1
                ? webUnlocked[0].title
                : `${webUnlocked[0].title} +${webUnlocked.length - 1} more`}
            </span>
          </span>
        </div>
      )}
      <div
        className="w-player"
        ref={playerRef}
        onMouseMove={bumpControls}
        onMouseEnter={bumpControls}
        onTouchStart={bumpControls}
        onClick={bumpControls}
        style={{
          // Hide the cursor too when controls are away, matching native
          // player UX. `none` on a div applies via CSS inheritance to the
          // video element and overlays.
          cursor: controlsVisible ? "default" : "none",
        }}
      >
        {paywalled && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              display: "grid",
              placeItems: "center",
              background:
                "radial-gradient(60% 80% at 50% 50%, #2A1F3A 0%, #0a0a0e 100%)",
              color: "#fff",
              padding: 32,
              textAlign: "center",
            }}
          >
            <div style={{ maxWidth: 520 }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>✨</div>
              <div
                style={{
                  fontFamily: "var(--display)",
                  fontSize: 36,
                  fontWeight: 900,
                  letterSpacing: "-0.01em",
                  marginBottom: 10,
                }}
              >
                This one's a Kidly+ video
              </div>
              <div
                style={{
                  fontSize: 15,
                  opacity: 0.78,
                  marginBottom: 22,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {trialEligible
                  ? "Unlock every premium video free for 30 days — no card needed. We'll only ask for payment if you decide to keep Kidly+."
                  : "Subscribe to Kidly+ to unlock premium videos. Cancel anytime."}
              </div>
              <button
                onClick={trialEligible ? startFreeTrial : startCheckout}
                style={{
                  border: 0,
                  padding: "14px 26px",
                  borderRadius: 14,
                  background: "var(--primary)",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "inset 0 -3px 0 rgba(0,0,0,0.15)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "inherit",
                }}
              >
                {trialEligible ? "Start 30-day free trial" : "Subscribe to Kidly+"}
              </button>
              {trialEligible && (
                <div
                  style={{
                    marginTop: 12,
                  }}
                >
                  <button
                    onClick={startCheckout}
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#fff",
                      opacity: 0.7,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontFamily: "inherit",
                    }}
                  >
                    Or subscribe now with a card
                  </button>
                </div>
              )}
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.55,
                  marginTop: 14,
                  fontWeight: 600,
                }}
              >
                {trialEligible
                  ? "No charge today · $9.99/mo only if you keep it"
                  : "$9.99/mo · Cancel anytime"}
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: activeQuestion ? 0.45 : 1,
            filter: activeQuestion ? "blur(2px)" : "none",
            transition: "opacity 240ms",
          }}
        >
          {hasVideo ? (
            <>
              <video
                ref={videoRef}
                src={playbackUrl}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  background: "#000",
                  // Bedtime: keep the element (audio plays) but hide the
                  // picture behind the calm overlay below.
                  opacity: isBedtime ? 0 : 1,
                }}
                onTimeUpdate={(e) =>
                  setSeconds(
                    Math.floor((e.target as HTMLVideoElement).currentTime),
                  )
                }
                onEnded={() => {
                  // Auto-advance to the next related item; if there's none,
                  // just stop on the last frame.
                  if (nextItem && onPlay) onPlay(nextItem);
                  else setPaused(true);
                }}
              />
              {isBedtime && (
                <div style={{ position: "absolute", inset: 0, zIndex: 40 }}>
                  <WebBedtimeAudio
                    title={detail?.title ?? video.title}
                    seconds={seconds}
                    duration={duration}
                    playing={!paused}
                    onTogglePlay={() => setPaused((p) => !p)}
                    onPause={() => setPaused(true)}
                    onSeek={(sec) => {
                      const v = videoRef.current;
                      if (v) v.currentTime = sec;
                      setSeconds(Math.floor(sec));
                    }}
                    tracks={upNext.map((v) => ({ id: v.id, title: v.title }))}
                    onSelect={(id) => {
                      const v = upNext.find((x) => x.id === id);
                      if (v && onPlay) onPlay(v);
                    }}
                    onNext={
                      nextItem && onPlay ? () => onPlay(nextItem) : undefined
                    }
                    onPrev={
                      prevItem && onPlay ? () => onPlay(prevItem) : undefined
                    }
                    onClose={onClose}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <WebVideoArt category={video.category} />
              {/* Idle mascot on the stage — hidden while a question is up so
                  it doesn't peek out behind the question card. */}
              {!activeQuestion && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 60,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div className={paused ? "" : "bob"}>
                    <WebMascot mood="happy" color="#FF8A4C" size={180} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Back"
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.5)",
            border: 0,
            color: "#fff",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            backdropFilter: "blur(6px)",
            zIndex: 2,
            opacity: controlsVisible ? 1 : 0,
            pointerEvents: controlsVisible ? "auto" : "none",
            transition: "opacity 240ms",
          }}
        >
          <span style={{ display: "block", transform: "rotate(180deg)" }}>
            <Icon name="chev" size={18} />
          </span>
        </button>

        {/* Question count pill */}
        {questions.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              padding: "8px 14px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.5)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.04em",
              backdropFilter: "blur(6px)",
              zIndex: 2,
            }}
          >
            {questions.length}{" "}
            {questions.length === 1 ? "question" : "questions"} in this video
          </div>
        )}

        {/* Live question overlay — bubble variant (mirrors QuestionOverlay.tsx) */}
        {activeQuestion && (
          <div
            className="fadeup"
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              padding: 36,
              zIndex: 3,
            }}
          >
            <div
              style={
                allImages
                  ? {
                      maxWidth: activeQuestion.imageUrl ? 400 : 420,
                      width: "100%",
                      // Only the scene questions (tall: scene image + faces)
                      // need a scroll cap. For plain picture questions we keep
                      // overflow visible so the answer tiles' selection ring +
                      // drop shadow aren't clipped. The scene case pads the
                      // box so the rings still clear the scroll edge.
                      ...(activeQuestion.imageUrl
                        ? {
                            maxHeight: "calc(100% - 8px)",
                            overflowY: "auto" as const,
                            padding: 14,
                            boxSizing: "border-box" as const,
                          }
                        : {}),
                    }
                  : {
                      background: "#fff",
                      borderRadius: 32,
                      padding: "24px 28px 28px",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.40)",
                      border: "4px solid var(--ink)",
                      maxWidth: 720,
                      width: "100%",
                    }
              }
            >
              {/* Scene illustration — depicts the situation (e.g. Benny's
                  tower fell over) so the kid sees what's happening before
                  matching the feeling. Inline data: URIs and absolute URLs
                  pass through; relative paths get the API host. */}
              {activeQuestion.imageUrl && (
                <div className="w-scene">
                  <img
                    src={
                      /^(https?:|data:)/.test(activeQuestion.imageUrl)
                        ? activeQuestion.imageUrl
                        : `${getApiBaseUrl()}${activeQuestion.imageUrl}`
                    }
                    alt=""
                  />
                </div>
              )}

              {/* Teaching narration — the mascot reads the prompt aloud and
                  bobs while talking. Tap it to hear the prompt again. Gated on
                  the parent's "Read questions aloud" setting; when off, the
                  question stays picture-only. */}
              {ttsEnabled && (activeQuestion.prompt ?? "").trim() && (
                <div className="w-teach-row">
                  <button
                    type="button"
                    className={"w-teach-mascot" + (speaking ? " talking" : "")}
                    onClick={repeatPrompt}
                    aria-label="Hear the question again"
                  >
                    <WebMascot mood="cheer" color="#FF8A4C" size={64} />
                  </button>
                  <div className="w-teach-bubble">{activeQuestion.prompt}</div>
                </div>
              )}

              {/* Option tiles */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${
                    questionOptions.length === 4 ? 2 : questionOptions.length
                  }, 1fr)`,
                  gap: 12,
                }}
              >
                {questionOptions.map((opt, i) => {
                  const hint = parseOption(opt.label);
                  const icon = stubIcon(opt.iconKey);
                  const hasIcon = !!icon;
                  // Parent-picked frame / scene images ride in via `imageUrl`
                  // (e.g. "Frame at 0:03") — highest-priority visual: show the
                  // picture, never the label.
                  // Absolute URLs (http) and inline data: URIs (our generated
                  // letter/number/color tiles) are used as-is; only relative
                  // backend paths get the API host prefixed.
                  const imgSrc = opt.imageUrl
                    ? /^(https?:|data:)/.test(opt.imageUrl)
                      ? opt.imageUrl
                      : `${getApiBaseUrl()}${opt.imageUrl}`
                    : null;
                  // Pre-readers can't read a word-answer — map plain words to a
                  // picture so every tile is answerable without reading.
                  const picUrl =
                    !icon && !imgSrc && hint.kind === "text"
                      ? optionPictureUrl(opt.label)
                      : null;
                  const resolved = answer.status !== "unanswered";
                  const selected =
                    answer.status === "unanswered"
                      ? undefined
                      : answer.selected;
                  const isCorrect = i === activeQuestion.correct;
                  const isPicked = i === selected;

                  let bg = "#FFF7EC";
                  let ring = "rgba(46,35,22,0.10)";
                  let label = "var(--ink)";
                  if (resolved) {
                    if (isCorrect) {
                      bg = "#E6F8DA";
                      ring = "#7AC74F";
                      label = "#2F5A1E";
                    } else if (isPicked) {
                      bg = "#FFE6E6";
                      ring = "#FF7A7A";
                      label = "#7A2E2E";
                    } else {
                      bg = "rgba(0,0,0,0.04)";
                      ring = "transparent";
                      label = "var(--ink-3)";
                    }
                  } else if (isPicked) {
                    bg = "#FFE2C2";
                    ring = "var(--primary)";
                  }
                  // In bare-image mode a faint/absent ring means "no border";
                  // only selection + answer feedback draw a visible ring.
                  const showRing =
                    ring !== "rgba(46,35,22,0.10)" && ring !== "transparent";

                  return (
                    <button
                      key={i}
                      onClick={() => handleAnswer(i)}
                      disabled={resolved}
                      className={
                        showHint && !resolved && isCorrect
                          ? "w-quiz-hint"
                          : undefined
                      }
                      style={{
                        border: 0,
                        background: allImages ? "transparent" : bg,
                        borderRadius: allImages ? 18 : 24,
                        // Square photo tiles (a bit smaller); the card width is
                        // capped above so 2×2 squares stay compact.
                        height: allImages ? undefined : 160,
                        aspectRatio: allImages ? "1 / 1" : undefined,
                        padding: allImages ? 0 : 16,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        cursor: resolved ? "default" : "pointer",
                        boxShadow: allImages
                          ? showRing
                            ? `0 0 0 5px ${ring}, 0 12px 26px rgba(0,0,0,0.38)`
                            : "0 12px 26px rgba(0,0,0,0.38)"
                          : `inset 0 0 0 4px ${ring}, 0 6px 0 rgba(0,0,0,0.06)`,
                        transform:
                          !resolved && isPicked ? "translateY(2px)" : "none",
                        transition:
                          "transform 120ms, box-shadow 120ms, background-color 200ms",
                        position: "relative",
                        overflow: "hidden",
                        fontFamily: "inherit",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          width: "100%",
                          display: "grid",
                          placeItems: "center",
                          padding: allImages
                            ? 0
                            : !hasIcon && !picUrl && !imgSrc && hint.kind === "text"
                              ? 0
                              : 6,
                        }}
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={opt.label}
                            style={{
                              width: allImages ? "100%" : "92%",
                              height: allImages ? "100%" : "92%",
                              objectFit: "cover",
                              borderRadius: allImages ? 18 : 12,
                            }}
                          />
                        ) : hasIcon ? (
                          <img
                            src={icon!.imageUrl}
                            alt={opt.label}
                            style={{
                              width: "78%",
                              height: "78%",
                              objectFit: "contain",
                            }}
                          />
                        ) : hint.kind !== "text" ? (
                          <OptionArt hint={hint} />
                        ) : picUrl ? (
                          <img
                            src={picUrl}
                            alt={opt.label}
                            style={{
                              width: "78%",
                              height: "78%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              fontFamily: "var(--display)",
                              fontWeight: 900,
                              fontSize: 28,
                              color: label,
                              textAlign: "center",
                              lineHeight: 1.05,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {opt.label}
                          </div>
                        )}
                      </div>

                      {!allImages && (hasIcon || hint.kind !== "text" || picUrl) && (
                        <div
                          style={{
                            fontFamily: "var(--display)",
                            fontWeight: 900,
                            fontSize: 20,
                            color: label,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {opt.label}
                        </div>
                      )}

                      {resolved && isCorrect && (
                        <div
                          className="pop"
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "#7AC74F",
                            display: "grid",
                            placeItems: "center",
                            boxShadow: "0 4px 0 rgba(0,0,0,0.10)",
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24">
                            <path
                              d="M5 12l5 5L20 7"
                              stroke="#fff"
                              strokeWidth="4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                          </svg>
                        </div>
                      )}
                      {resolved && !isCorrect && isPicked && (
                        <div
                          className="pop"
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "#FF7A7A",
                            display: "grid",
                            placeItems: "center",
                            color: "#fff",
                            fontWeight: 900,
                            fontSize: 22,
                          }}
                        >
                          ×
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {answer.status !== "unanswered" && (
                <div
                  className="fadeup"
                  style={{
                    marginTop: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                    fontFamily: "var(--display)",
                    fontSize: 22,
                    fontWeight: 900,
                    color:
                      answer.status === "correct" ? "#3A8530" : "var(--ink-2)",
                  }}
                >
                  {answer.status === "correct" ? (
                    <>
                      <span className="pop">
                        <WebMascot
                          mood="cheer"
                          color="var(--primary)"
                          size={52}
                        />
                      </span>
                      Nice! ⭐ +1 star
                    </>
                  ) : (
                    <>
                      <WebMascot
                        mood="happy"
                        color="var(--primary)"
                        size={48}
                      />
                      Almost — keep watching!
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress bar (hidden while question is up, also auto-fades after
            3s of inactivity per the controlsVisible gate). */}
        {!activeQuestion && (
          <div
            style={{
              position: "absolute",
              left: 20,
              right: 20,
              bottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "10px 16px",
              opacity: controlsVisible ? 1 : 0,
              pointerEvents: controlsVisible ? "auto" : "none",
              transition: "opacity 240ms",
              background: "rgba(0,0,0,0.5)",
              borderRadius: 14,
              backdropFilter: "blur(8px)",
              color: "#fff",
            }}
          >
            <button
              onClick={() => setPaused((p) => !p)}
              style={{
                border: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#fff",
                color: "var(--ink)",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              <Icon name={paused ? "play" : "pause"} size={16} />
            </button>
            <div
              style={{ flex: 1, padding: "8px 0", cursor: "pointer" }}
              onClick={(e) => {
                // Click-to-seek. Resolve relative to the inner track so the
                // ratio is accurate regardless of the outer padding we added
                // to enlarge the hit-zone.
                const rect = (
                  e.currentTarget.querySelector(
                    "[data-track]",
                  ) as HTMLDivElement | null
                )?.getBoundingClientRect();
                if (!rect || rect.width === 0) return;
                const ratio = Math.max(
                  0,
                  Math.min(1, (e.clientX - rect.left) / rect.width),
                );
                const t = ratio * duration;
                const v = videoRef.current;
                if (v) {
                  v.currentTime = t;
                }
                setSeconds(Math.floor(t));
              }}
            >
              <div
                data-track
                style={{
                  height: 6,
                  background: "rgba(255,255,255,0.25)",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progress * 100}%`,
                    height: "100%",
                    background: "var(--star)",
                    transition: "width 400ms linear",
                  }}
                />
              </div>
              {/* Question markers */}
              {questions.length > 0 && (
                <div
                  style={{
                    position: "relative",
                    height: 0,
                    marginTop: -6,
                    pointerEvents: "none",
                  }}
                >
                  {questions.map((q) => (
                    <span
                      key={q.id}
                      title={q.prompt}
                      style={{
                        position: "absolute",
                        left: `${(q.tSec / duration) * 100}%`,
                        top: -3,
                        transform: "translateX(-50%)",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "var(--primary)",
                        boxShadow: "0 0 0 2px rgba(0,0,0,0.4)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                opacity: 0.85,
                minWidth: 92,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtClock(seconds)} / {fmtClock(duration)}
            </div>
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              style={{
                border: 0,
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              {/* Inline SVG — keep this file dependency-light. Corner-bracket
                  icon flips meaning via the `isFullscreen` flag. */}
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="w-player-meta">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {video.category}
          </span>
          <span
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {videoMinutes(duration)} min · Age {video.ageMin ?? 3}–
            {video.ageMax ?? 6}
            {questions.length > 0 &&
              ` · ${questions.length} question${questions.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <h1>{video.title}</h1>
        <p>
          Reviewed by a child-development expert. No ads. Pauses for
          age-appropriate questions to make sure your child is following along.
        </p>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            className="btn"
            onClick={handlePlayOnTv}
            disabled={casting}
            title={
              tvOnline ? "Send this video to the paired TV" : "No TV connected"
            }
            style={{
              opacity: !tvOnline || casting ? 0.6 : 1,
              cursor: !tvOnline || casting ? "not-allowed" : "pointer",
            }}
          >
            <Icon name="cast" size={16} />{" "}
            {casting
              ? "Sending…"
              : tvOnline
                ? "Play on TV"
                : "Play on TV (no TV)"}
          </button>
          <button
            className="btn btn-ghost"
            onClick={openPlaylistPicker}
            style={{
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.2)",
              cursor: "pointer",
            }}
          >
            <Icon name="plus" size={16} /> Add to playlist
          </button>
        </div>

        {/* Up next — more like this. Audio (bedtime) gets its own up-next
            list inside the audio player, so only show this for video. */}
        {!isBedtime && upNext.length > 0 && onPlay && (
          <div style={{ marginTop: 36 }}>
            <h2
              style={{
                fontFamily: "var(--display)",
                fontSize: 18,
                fontWeight: 900,
                color: "#fff",
                margin: "0 0 14px",
              }}
            >
              Up next
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxWidth: 560,
              }}
            >
              {upNext.map((v) => {
                const thumb = v.thumbUrl
                  ? v.thumbUrl.startsWith("http")
                    ? v.thumbUrl
                    : `${getApiBaseUrl()}${v.thumbUrl}`
                  : null;
                return (
                  <button
                    key={v.id}
                    onClick={() => onPlay(v)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      width: "100%",
                      textAlign: "left",
                      border: 0,
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 14,
                      padding: 8,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "background-color 140ms",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
                    }
                  >
                    <div
                      style={{
                        position: "relative",
                        width: 124,
                        height: 70,
                        borderRadius: 10,
                        overflow: "hidden",
                        flex: "0 0 auto",
                        background: "#1a1a22",
                      }}
                    >
                      {thumb ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={thumb}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <WebVideoArt category={v.category} />
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          color: "#fff",
                          fontWeight: 800,
                          fontSize: 15,
                          lineHeight: 1.25,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {v.title}
                      </div>
                      <div
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 12.5,
                          fontWeight: 600,
                          marginTop: 3,
                        }}
                      >
                        {videoMinutes(v.duration)} min ·{" "}
                        {v.category}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add-to-playlist picker — choose an existing playlist or make a new
          one, then append this video via the playlists API. */}
      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            background: "rgba(0,0,0,0.55)",
            display: "grid",
            placeItems: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 24,
              padding: 24,
              width: "100%",
              maxWidth: 440,
              boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--display)",
                fontSize: 22,
                fontWeight: 900,
                color: "var(--ink)",
              }}
            >
              Add to playlist
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink-3)",
                margin: "4px 0 16px",
              }}
            >
              Save “{video.title}” to a playlist.
            </div>

            {playlists === null ? (
              <div
                style={{ padding: 16, color: "var(--ink-3)", fontWeight: 700 }}
              >
                Loading…
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {playlists.map((pl) => {
                  const already = pl.items.some(
                    (it) => it.videoId === video.id,
                  );
                  return (
                    <button
                      key={pl.id}
                      onClick={() => !already && addToPlaylist(pl)}
                      disabled={plBusy || already}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 14px",
                        borderRadius: 14,
                        border: "1.5px solid var(--line-strong)",
                        background: "var(--bg-warm)",
                        cursor: already ? "default" : "pointer",
                        opacity: already ? 0.6 : 1,
                        fontFamily: "inherit",
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 4,
                          background: pl.color || "var(--primary)",
                          flex: "0 0 auto",
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          textAlign: "left",
                          fontWeight: 800,
                          color: "var(--ink)",
                        }}
                      >
                        {pl.name}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: already ? "#3A8530" : "var(--ink-3)",
                        }}
                      >
                        {already ? "✓ Added" : `${pl.items.length}`}
                      </span>
                    </button>
                  );
                })}
                {playlists.length === 0 && (
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--ink-3)",
                      padding: "2px 2px 6px",
                    }}
                  >
                    No playlists yet — create your first below.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") createAndAdd();
                }}
                placeholder="New playlist name"
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1.5px solid var(--line-strong)",
                  fontSize: 14,
                  fontFamily: "inherit",
                }}
              />
              <button
                onClick={createAndAdd}
                disabled={plBusy || !newName.trim()}
                style={{
                  border: 0,
                  borderRadius: 12,
                  padding: "0 16px",
                  background: "var(--primary)",
                  color: "#fff",
                  fontWeight: 800,
                  cursor: newName.trim() ? "pointer" : "not-allowed",
                  opacity: newName.trim() ? 1 : 0.6,
                  fontFamily: "inherit",
                }}
              >
                Create + add
              </button>
            </div>

            <button
              onClick={() => setPickerOpen(false)}
              style={{
                marginTop: 14,
                border: 0,
                background: "transparent",
                color: "var(--ink-3)",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fadeup"
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 60,
            background: "var(--ink)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 14,
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default WebPlayer;
